import { DrawerMenuItemList } from 'Components/Layout/DrawerMenu/DrawerMenuItemList'
import type { NavLink as NavLinkType } from 'Components/Layout/DrawerMenu/types'
import { WithLoader } from 'Components/WithLoader'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import { useTariffs } from 'Services/ProductManagement/Features/Tariff/Hooks'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import { PRODUCT_MANAGEMENT_ROOT_URL } from 'Util/constants'
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  styled,
  Typography
} from '@mui/material'
import { useContext } from 'react'
import { NavLink } from 'react-router-dom'
import { t } from 'ttag'

const StyledMenuItem = styled(MenuItem)(() => ({
  padding: 0
}))

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.text.primary,
  width: '100%',
  padding: theme.spacing(1, 2)
}))

function TariffDrawerMenuContent() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const { selectedTariffId, setSelectedTariffId } = useContext(TariffContext)
  const drawerMenuNavLinks: NavLinkType[] = [
    {
      title: t`Tariff products`,
      link: `${baseUrl}/${selectedTariffId}/products`
    },
    {
      title: t`Tariff details`,
      link: `${baseUrl}/${selectedTariffId}/details`
    },
    {
      title: t`Tariff sales channels`,
      link: `${baseUrl}/${selectedTariffId}/sales-channels`
    }
  ]

  const { allTariffs, allTariffsLoading } = useTariffs()
  const handleTariffSelectChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    setSelectedTariffId(value)
  }

  const isLoading = allTariffsLoading === 'pending'

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h3" noWrap component="div" sx={{ fontFamily: 'Prompt', mt: 1, mb: 3 }}>
          {t`Tariffs`}
        </Typography>
        <WithLoader loading={isLoading} rows={1}>
          <FormControl sx={{ mb: 2, width: '100%' }}>
            <InputLabel shrink>{t`Tariff`}</InputLabel>
            <Select
              sx={{ backgroundColor: 'background.default', a: { padding: 0 } }}
              value={selectedTariffId}
              label={t`Tariffs`}
              displayEmpty
              onChange={handleTariffSelectChange}
              input={<OutlinedInput notched label={t`Tariffs`} />}
            >
              <StyledMenuItem value="">
                <StyledNavLink to={baseUrl}>{t`All tariffs`}</StyledNavLink>
              </StyledMenuItem>
              {allTariffs.map((tariff: Tariff) => (
                <StyledMenuItem key={tariff.id} value={tariff.id}>
                  <StyledNavLink to={`${baseUrl}/${tariff.id}/products`}>
                    {tariff.description}
                  </StyledNavLink>
                </StyledMenuItem>
              ))}
            </Select>
          </FormControl>
        </WithLoader>
      </Box>
      <Divider />
      {selectedTariffId && <DrawerMenuItemList navLinks={drawerMenuNavLinks} rootUrl={baseUrl} />}
    </>
  )
}

export default TariffDrawerMenuContent
